import { SpeciesEnum, DentalChartEnum } from "requests";
import { StoredDentalChartings, StoredMedicalCase } from "requests/fragments";
import {
  BONE_LOSS_KEYS,
  CAT_ADULT_TEETH,
  CAT_MILK_TEETH,
  DEEP_POCKET_VALUE_FOR_CAT_AND_RABBIT,
  DEEP_POCKET_VALUE_FOR_DOG,
  DOG_ADULT_TEETH,
  DOG_MILK_TEETH,
  RABBIT_TEETH
} from "../constants";

export const isToothFilled = (
  tooth: NonNullable<StoredDentalChartings>[number],
  checkState = false
) => {
  const fieldsToCheckForNull: (keyof NonNullable<
    StoredDentalChartings
  >[number])[] = [
    "periodontal",
    "furcation",
    "mobility",
    "gingivitis",
    "enamel",
    "fracture",
    "resorption",
    "calculus",
    "periodontalPocketMBuc",
    "periodontalPocketBuccal",
    "periodontalPocketDBuc",
    "periodontalPocketMLingPal",
    "periodontalPocketLingPal",
    "periodontalPocketDLingPal",
    "gingivalRecessionMBuc",
    "gingivalRecessionBuccal",
    "gingivalRecessionDBuc",
    "gingivalRecessionMLingPal",
    "gingivalRecessionLingPal",
    "gingivalRecessionDLingPal",
    "sutureMaterial",
    "restoratives",
    "crCementation",
    "obturation",
    "distalWorkingLength",
    "mesialWorkingLength",
    "palatalWorkingLength",
    "distalMarFile",
    "mesialMarFile",
    "palatalMarFile"
  ];

  const fieldsToCheckForEmptyString: (keyof NonNullable<
    StoredDentalChartings
  >[number])[] = ["notesTimer", "treatmentComment", "technique"];

  const fieldsToCheckForEmptyArray = [
    "other",
    "oralSurgeries",
    "restorations",
    "endodontics"
  ] as const;

  const baseInfo =
    fieldsToCheckForNull.some(field => tooth[field] != null) ||
    fieldsToCheckForEmptyArray.some(field => tooth[field].length > 0) ||
    fieldsToCheckForEmptyString.some(
      field => tooth[field] !== "" && tooth[field] !== null
    ) ||
    tooth.timerDurationSec > 0;

  return checkState ? baseInfo || tooth.toothState !== null : baseInfo;
};

export const getCaseImageIds = (medCase: StoredMedicalCase) => {
  const {
    image,
    generalAssessment,
    calculationAgreement,
    dentalSketch
  } = medCase.client.patient;
  const { disclaimer } = medCase.client;

  return [
    typeof image === "number" ? image : null,

    typeof generalAssessment?.beforePhotoFront === "number"
      ? generalAssessment.beforePhotoFront
      : null,

    typeof generalAssessment?.beforePhotoLeft === "number"
      ? generalAssessment.beforePhotoLeft
      : null,

    typeof generalAssessment?.beforePhotoRight === "number"
      ? generalAssessment.beforePhotoRight
      : null,

    typeof generalAssessment?.afterPhotoFront === "number"
      ? generalAssessment.afterPhotoFront
      : null,

    typeof generalAssessment?.afterPhotoLeft === "number"
      ? generalAssessment.afterPhotoLeft
      : null,

    typeof generalAssessment?.afterPhotoRight === "number"
      ? generalAssessment.afterPhotoRight
      : null,

    ...(generalAssessment?.additionalPhotos?.map(photo =>
      typeof photo === "number" ? photo : null
    ) || []),

    ...(generalAssessment?.radiographicFindingsPhotos?.map(photo =>
      typeof photo === "number" ? photo : null
    ) || []),

    typeof dentalSketch === "number" ? dentalSketch : null,

    typeof disclaimer?.signature === "number" ? disclaimer.signature : null,

    typeof calculationAgreement?._id === "number"
      ? calculationAgreement._id
      : null
  ].filter(Boolean) as number[];
};

export const hasToothDeepPockets = (
  tooth: NonNullable<StoredDentalChartings>[number],
  species: SpeciesEnum | null
) => {
  const fieldsToCheckForAbnormalities = [
    "periodontal",
    "furcation",
    "mobility",
    "gingivitis",
    "calculus",
    "enamel",
    "fracture",
    "resorption"
  ] as const;
  const hasAbnormalities =
    fieldsToCheckForAbnormalities.some(field => tooth[field] != null) ||
    tooth.other.length > 0;

  const hasDeepPockets = [
    tooth.periodontalPocketMLingPal,
    tooth.periodontalPocketMBuc,
    tooth.periodontalPocketDBuc,
    tooth.periodontalPocketBuccal,
    tooth.periodontalPocketDLingPal,
    tooth.periodontalPocketLingPal,
    tooth.gingivalRecessionMLingPal,
    tooth.gingivalRecessionMBuc,
    tooth.gingivalRecessionDBuc,
    tooth.gingivalRecessionBuccal,
    tooth.gingivalRecessionDLingPal,
    tooth.gingivalRecessionLingPal
  ].some(diagnosis => {
    if (diagnosis && species === SpeciesEnum.Dog) {
      return diagnosis >= DEEP_POCKET_VALUE_FOR_DOG;
    }
    if (
      diagnosis &&
      (species === SpeciesEnum.Cat || species === SpeciesEnum.Rabbit)
    ) {
      return diagnosis >= DEEP_POCKET_VALUE_FOR_CAT_AND_RABBIT;
    }
    return false;
  });

  return hasAbnormalities || hasDeepPockets;
};

export const getSpeciesTeeth = (
  species: SpeciesEnum | null,
  milkOrAdultTeeth: DentalChartEnum | null
) => {
  const speciesTeethNumbers: {
    [species in SpeciesEnum]?: { [chart in DentalChartEnum]: number[] };
  } = {
    [SpeciesEnum.Dog]: {
      [DentalChartEnum.Deciduous]: DOG_MILK_TEETH,
      [DentalChartEnum.Adult]: DOG_ADULT_TEETH
    },
    [SpeciesEnum.Cat]: {
      [DentalChartEnum.Deciduous]: CAT_MILK_TEETH,
      [DentalChartEnum.Adult]: CAT_ADULT_TEETH
    }
  };

  return species && milkOrAdultTeeth
    ? speciesTeethNumbers[species]?.[milkOrAdultTeeth] || RABBIT_TEETH
    : [];
};

export const getEmptyBoneLoss = () => {
  return BONE_LOSS_KEYS.reduce((acc, key) => {
    acc[key] = [];
    return acc;
  }, {} as Record<string, string[]>);
};
